import React, { useState } from 'react';
import { Activity, Heart, Brain, AlertCircle, Pill, Video, ChevronDown, ChevronUp, Award, Check } from 'lucide-react';
import { triggerSubtleConfetti } from '../utils/confetti';
import { trackEvent, EVENTS } from '../utils/posthog';
// Enhanced competency card with expanded information
const CompetencyCard = ({
  icon,
  title,
  percentage,
  description,
  skills,
  relevanceSG,
  color
}) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = icon;
  const handleToggleExpand = e => {
    e.stopPropagation();
    setExpanded(!expanded);
    if (!expanded) {
      // Track competency card expansion
      trackEvent(EVENTS.COMPETENCY_CARD_EXPANDED, {
        competency_title: title,
        competency_percentage: percentage
      });
    }
    triggerSubtleConfetti(e.currentTarget);
  };
  return <div className={`bg-white rounded-card shadow-card hover:shadow-glow-mint transition-all duration-300 ${expanded ? 'shadow-glow-mint' : 'hover:-translate-y-1'}`} data-id="element-125">
      <div className="p-6" data-id="element-126">
        <div className="flex items-start justify-between" data-id="element-127">
          <div className="flex items-start" data-id="element-128">
            <div className="w-12 h-12 rounded-full bg-sage-mint/10 flex items-center justify-center mr-4 group-hover:bg-sage-mint/20 transition-all duration-300" data-id="element-129">
              <Icon className="h-6 w-6 text-coral-pink" data-id="element-130" />
            </div>
            <div data-id="element-131">
              <h3 className="text-lg font-medium text-charcoal" data-id="element-132">{title}</h3>
              <div className="mt-1 flex items-center" data-id="element-133">
                <span className="text-sm font-semibold text-coral-pink" data-id="element-134">
                  {percentage}%
                </span>
                <div className="ml-2 w-24 h-1 bg-grey-400/20 rounded-full" data-id="element-135">
                  <div className="h-1 bg-sage-mint rounded-full transition-all duration-700" style={{
                  width: `${percentage}%`
                }} data-id="element-136"></div>
                </div>
              </div>
            </div>
          </div>
          <button onClick={handleToggleExpand} className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${expanded ? 'bg-sage-mint text-white' : 'bg-sage-mint/10 text-sage-mint hover:bg-sage-mint/20'}`} data-id="element-137">
            {expanded ? <ChevronUp size={18} data-id="element-138" /> : <ChevronDown size={18} data-id="element-139" />}
          </button>
        </div>
        {/* Basic description - always visible */}
        <p className="mt-4 text-sm text-grey-600" data-id="element-140">{description}</p>
        {/* Expanded content */}
        {expanded && <div className="mt-6 pt-4 border-t border-grey-400/20 animate-fadeIn" data-id="element-141">
            {/* Key skills */}
            <div className="mb-4" data-id="element-142">
              <h4 className="text-sm font-medium text-charcoal mb-2" data-id="element-143">
                Key Skills & Certifications
              </h4>
              <ul className="grid grid-cols-1 gap-2" data-id="element-144">
                {skills.map((skill, index) => <li key={index} className="flex items-start" data-id="element-145">
                    <Check className="h-4 w-4 text-sage-mint mr-2 mt-0.5 flex-shrink-0" data-id="element-146" />
                    <span className="text-sm text-grey-600" data-id="element-147">{skill}</span>
                  </li>)}
              </ul>
            </div>
            {/* Singapore relevance */}
            <div className="bg-sage-mint/10 p-4 rounded-lg" data-id="element-148">
              <h4 className="text-sm font-medium text-charcoal mb-2 flex items-center" data-id="element-149">
                <Award className="h-4 w-4 text-sage-mint mr-2" data-id="element-150" />
                Relevance to Singapore
              </h4>
              <p className="text-sm text-grey-600" data-id="element-151">{relevanceSG}</p>
            </div>
          </div>}
      </div>
    </div>;
};
const ClinicalDashboard = () => {
  // Enhanced competencies data with more detailed information
  const competencies = [{
    icon: AlertCircle,
    title: 'Emergency Care',
    percentage: 85,
    description: 'Rapid assessment and intervention in critical situations, ensuring patient stabilization and appropriate care pathways.',
    skills: ['ABCDE assessment protocol mastery', 'Neurological emergency management', 'ECG interpretation & cardiac care', 'Systematic vital functions monitoring'],
    relevanceSG: "Critical for Singapore's emergency response system and A&E departments, especially with the increasing demand for urgent care services in urban healthcare settings."
  }, {
    icon: Heart,
    title: 'Geriatric Care',
    percentage: 90,
    description: 'Specialized care for elderly patients with complex needs, focusing on dignity, independence and quality of life.',
    skills: ['Geriatric frailty assessment', 'Polypharmacy management', 'Fall prevention protocols', 'End-of-life care coordination'],
    relevanceSG: "Essential for Singapore's rapidly aging population (fastest aging society in Asia), supporting the government's initiatives for elder care excellence."
  }, {
    icon: Brain,
    title: 'Mental Health',
    percentage: 80,
    description: 'Holistic approach to psychological wellbeing, combining clinical expertise with cultural sensitivity and patient dignity.',
    skills: ['Youth psychiatric assessment', 'Anxiety & depression management', 'Cultural sensitivity in mental health', 'Motivational interviewing techniques'],
    relevanceSG: "Addresses Singapore's growing focus on mental wellbeing, particularly in high-pressure environments and among youth and elderly populations."
  }, {
    icon: Activity,
    title: 'Critical Skills',
    percentage: 90,
    description: 'Advanced clinical capabilities for high-acuity settings, ensuring precise care delivery in complex medical situations.',
    skills: ['Central venous catheter management', 'Systematic patient assessment', 'Advanced vital signs interpretation', 'Critical care documentation'],
    relevanceSG: "Supports Singapore's world-class tertiary hospitals and specialized care units, maintaining the high standards of clinical excellence."
  }, {
    icon: Pill,
    title: 'Medication Mgmt',
    percentage: 85,
    description: 'Expert administration and monitoring of pharmaceutical interventions, with special focus on safety and efficacy.',
    skills: ['Geriatric pharmacotherapy', 'Medication safety protocols', 'Adverse reaction management', 'Patient medication education'],
    relevanceSG: "Crucial for medication safety in Singapore's healthcare system, particularly for elderly patients with multiple prescriptions and chronic conditions."
  }, {
    icon: Video,
    title: 'Telehealth Ready',
    percentage: 75,
    description: 'Digital healthcare delivery skills enabling remote assessment, monitoring, and patient education through technology.',
    skills: ['Remote patient assessment', 'Digital health documentation', 'Virtual consultation techniques', 'Technology-assisted patient education'],
    relevanceSG: "Aligns with Singapore's Smart Nation initiatives and the growing telehealth sector, especially important after COVID-19 accelerated digital healthcare adoption."
  }];
  const handleViewAll = e => {
    triggerSubtleConfetti(e.currentTarget);
  };
  return <section id="expertise" className="py-section bg-cream-white relative" data-id="element-152">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMwLTkuOTQtOC4wNi0xOC0xOC0xOHY2QzI0LjI4IDYgMzAgMTEuNzIgMzAgMThIMzZ6IiBmaWxsPSIjQkFERkRCIiBmaWxsLW9wYWNpdHk9Ii4wNSIvPjwvZz48L3N2Zz4=')] opacity-5" data-id="element-153"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-154">
        {/* Section Header Card - SEO optimized H2 */}
        <div className="bg-white rounded-card shadow-card p-6 mb-8 border-l-4 border-sage-mint hover:shadow-glow-mint transition-all duration-300" data-id="element-155">
          <div className="flex flex-col md:flex-row md:items-center justify-between" data-id="element-156">
            <h2 className="text-2xl md:text-3xl font-light text-charcoal" data-id="element-157">
              Clinical Expertise: Geriatric Care, Patient Safety & Emergency
              Nursing
            </h2>
            <button className="text-coral-pink text-sm font-medium mt-2 md:mt-0 hover:text-sage-mint transition-all duration-300 relative group" onClick={handleViewAll} data-id="element-158">
              View All Specializations
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-sage-mint group-hover:w-full transition-all duration-300" data-id="element-159"></span>
            </button>
          </div>
        </div>
        {/* Competency Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-160">
          {competencies.map((competency, index) => <CompetencyCard key={index} icon={competency.icon} title={competency.title} percentage={competency.percentage} description={competency.description} skills={competency.skills} relevanceSG={competency.relevanceSG} color="sage-mint" data-id="element-161" />)}
        </div>
      </div>
    </section>;
};
export default ClinicalDashboard;